/**
 * Menu Scene - PREMIUM VISUAL DESIGN
 * 
 * First impression is best impression!
 * Features:
 * - Animated nebula gradient background
 * - Shooting stars
 * - Glowing 3D planet centerpiece
 * - Pulsing play button with glow
 * - Particle sparkles
 * - Smooth animations
 */

import Phaser from 'phaser';
import { SCENES, UI, GAME_NAME } from '../config/Constants';
import { AudioManager } from '../systems/AudioManager';

export class MenuScene extends Phaser.Scene {
  private title!: Phaser.GameObjects.Text;
  private playButton!: Phaser.GameObjects.Container;
  private settingsButton!: Phaser.GameObjects.Container;
  private planets: Phaser.GameObjects.Container[] = [];
  private shootingStarTimer?: Phaser.Time.TimerEvent;
  private audioManager!: AudioManager;
  
  private get width(): number {
    return this.scale.width;
  }
  
  private get height(): number {
    return this.scale.height;
  }
  
  private get textRes(): number {
    return Math.min(window.devicePixelRatio || 1, 2);
  }
  
  constructor() {
    super({ key: SCENES.MENU });
  }
  
  create(): void {
    console.log('[MenuScene] Creating premium menu...', { width: this.width, height: this.height });
    
    // Initialize audio and start background music
    this.audioManager = AudioManager.getInstance(this);
    this.audioManager.startMusic();
    
    // Layered background with nebula effect
    this.createNebulaBackground();
    
    // Twinkling stars
    this.createAnimatedStars();
    
    // Shooting stars
    this.startShootingStars();
    
    // Floating 3D planets
    this.createFloatingPlanets();
    
    // Central hero planet
    this.createHeroPlanet();
    
    // Title with glow effect
    this.createTitle();
    
    // Premium play button
    this.createPlayButton();
    
    // High score display
    this.createHighScoreDisplay();
    
    // Settings button
    this.createSettingsButton();
    
    // Particle sparkles
    this.createSparkles();
    
    // Setup input handlers
    this.setupInput();
    
    // Handle resize
    this.scale.on('resize', this.handleResize, this);
    
    // Entrance animation
    this.playEntranceAnimation();
    
    console.log('[MenuScene] Premium menu ready');
  }
  
  private handleResize(): void {
    console.log('[MenuScene] Resize detected');
  }
  
  private createNebulaBackground(): void {
    const graphics = this.add.graphics();
    
    // Deep space base
    graphics.fillStyle(0x050510, 1);
    graphics.fillRect(0, 0, this.width, this.height);
    
    // Layered nebula gradients
    const centerX = this.width / 2;
    const centerY = this.height * 0.4;
    
    // Purple nebula (top)
    for (let i = 0; i < 8; i++) {
      const alpha = 0.03 - (i * 0.003);
      const radius = 300 + i * 80;
      graphics.fillStyle(0x6366f1, alpha);
      graphics.fillCircle(centerX - 100, centerY - 100, radius);
    }
    
    // Pink nebula (right)
    for (let i = 0; i < 6; i++) {
      const alpha = 0.025 - (i * 0.003);
      const radius = 250 + i * 60;
      graphics.fillStyle(0xec4899, alpha);
      graphics.fillCircle(this.width * 0.8, centerY + 50, radius);
    }
    
    // Cyan accent (bottom)
    for (let i = 0; i < 5; i++) {
      const alpha = 0.02 - (i * 0.003);
      const radius = 200 + i * 50;
      graphics.fillStyle(0x06b6d4, alpha);
      graphics.fillCircle(this.width * 0.3, this.height * 0.7, radius);
    }
  }
  
  private createAnimatedStars(): void {
    // Reduced count for better FPS
    const starCount = Math.floor((this.width * this.height) / 12000);
    
    for (let i = 0; i < starCount; i++) {
      const x = Phaser.Math.Between(0, this.width);
      const y = Phaser.Math.Between(0, this.height);
      const size = Phaser.Math.FloatBetween(0.5, 2.5);
      const alpha = Phaser.Math.FloatBetween(0.3, 1);
      
      const star = this.add.circle(x, y, size, 0xffffff, alpha);
      
      // Twinkle animation
      this.tweens.add({
        targets: star,
        alpha: { from: alpha * 0.3, to: alpha },
        duration: Phaser.Math.Between(1000, 3000),
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: Phaser.Math.Between(0, 2000)
      });
    }
  }
  
  private startShootingStars(): void {
    this.shootingStarTimer = this.time.addEvent({
      delay: 4000, // Reduced frequency for performance
      callback: this.createShootingStar,
      callbackScope: this,
      loop: true
    });
    
    // Create first one immediately
    this.time.delayedCall(500, () => this.createShootingStar());
  }
  
  private createShootingStar(): void {
    const startX = Phaser.Math.Between(this.width * 0.2, this.width);
    const startY = Phaser.Math.Between(0, this.height * 0.4);
    
    const star = this.add.graphics();
    star.fillStyle(0xffffff, 1);
    star.fillCircle(0, 0, 2);
    
    // Tail gradient
    for (let i = 1; i <= 15; i++) {
      star.fillStyle(0xffffff, 1 - (i * 0.06));
      star.fillCircle(i * 3, i * 2, 2 - (i * 0.1));
    }
    
    star.setPosition(startX, startY);
    star.setDepth(5);
    
    this.tweens.add({
      targets: star,
      x: startX - 300,
      y: startY + 200,
      alpha: 0,
      duration: 800,
      ease: 'Quad.easeOut',
      onComplete: () => star.destroy()
    });
  }
  
  private createFloatingPlanets(): void {
    const planetConfigs = [
      { x: 0.15, y: 0.25, radius: 25, colors: [0x6366f1, 0x4f46e5], hasRing: true },
      { x: 0.85, y: 0.35, radius: 35, colors: [0xf97316, 0xea580c], hasRing: false },
      { x: 0.12, y: 0.65, radius: 20, colors: [0x14b8a6, 0x0d9488], hasRing: false },
      { x: 0.88, y: 0.70, radius: 30, colors: [0xec4899, 0xdb2777], hasRing: true },
      { x: 0.25, y: 0.85, radius: 22, colors: [0x8b5cf6, 0x7c3aed], hasRing: false },
    ];
    
    planetConfigs.forEach((config, index) => {
      const x = this.width * config.x;
      const y = this.height * config.y;
      
      const container = this.add.container(x, y);
      container.setDepth(10);
      
      // Glow
      const glow = this.add.circle(0, 0, config.radius * 2, config.colors[0], 0.15);
      
      // Planet body
      const body = this.add.circle(0, 0, config.radius, config.colors[0], 1);
      body.setStrokeStyle(2, config.colors[1], 0.8);
      
      // Highlight
      const highlight = this.add.arc(
        -config.radius * 0.3, -config.radius * 0.3,
        config.radius * 0.5, 200, 320, false, 0xffffff, 0.3
      );
      
      container.add([glow, body, highlight]);
      
      // Ring if applicable
      if (config.hasRing) {
        const ring = this.add.ellipse(0, 5, config.radius * 2.5, config.radius * 0.6, config.colors[1], 0.4);
        ring.setStrokeStyle(2, config.colors[0], 0.6);
        container.add(ring);
      }
      
      this.planets.push(container);
      
      // Float animation
      this.tweens.add({
        targets: container,
        y: y + Phaser.Math.Between(-25, 25),
        duration: Phaser.Math.Between(3000, 5000),
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1
      });
      
      // Slow rotation
      this.tweens.add({
        targets: container,
        angle: index % 2 === 0 ? 360 : -360,
        duration: 60000,
        repeat: -1
      });
      
      // Pulse glow
      this.tweens.add({
        targets: glow,
        alpha: { from: 0.1, to: 0.25 },
        scale: { from: 1, to: 1.1 },
        duration: 2000,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1
      });
    });
  }
  
  private createHeroPlanet(): void {
    const centerX = this.width / 2;
    const centerY = this.height * 0.42;
    const radius = Math.min(this.width, this.height) * 0.12;
    
    const container = this.add.container(centerX, centerY);
    container.setDepth(20);
    
    // Outer glow layers
    for (let i = 4; i >= 0; i--) {
      const glowSize = radius * (1.5 + i * 0.3);
      const glowAlpha = 0.08 - (i * 0.015);
      const glow = this.add.circle(0, 0, glowSize, 0x00d4ff, glowAlpha);
      container.add(glow);
      
      this.tweens.add({
        targets: glow,
        scale: { from: 1, to: 1.15 },
        alpha: { from: glowAlpha, to: glowAlpha * 0.5 },
        duration: 2500 + i * 200,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1
      });
    }
    
    // Shadow underneath
    const shadow = this.add.ellipse(0, radius * 0.9, radius * 2, radius * 0.4, 0x000000, 0.3);
    container.add(shadow);
    
    // Main planet body with gradient effect (layered circles)
    const bodyDark = this.add.circle(0, 0, radius, 0x0891b2, 1);
    const bodyLight = this.add.circle(-radius * 0.15, -radius * 0.15, radius * 0.9, 0x06b6d4, 0.7);
    const bodyHighlight = this.add.circle(-radius * 0.3, -radius * 0.3, radius * 0.5, 0x22d3ee, 0.5);
    
    // Surface details
    const crater1 = this.add.circle(radius * 0.3, radius * 0.1, radius * 0.15, 0x0e7490, 0.6);
    const crater2 = this.add.circle(-radius * 0.1, radius * 0.4, radius * 0.08, 0x0e7490, 0.5);
    
    container.add([bodyDark, bodyLight, bodyHighlight, crater1, crater2]);
    
    // Ring
    const ring = this.add.ellipse(0, 10, radius * 3, radius * 0.7, 0x00d4ff, 0.2);
    ring.setStrokeStyle(3, 0x22d3ee, 0.6);
    container.add(ring);
    
    // Specular highlight
    const specular = this.add.arc(
      -radius * 0.4, -radius * 0.4,
      radius * 0.4, 200, 280, false, 0xffffff, 0.4
    );
    container.add(specular);
    
    // Gentle floating
    this.tweens.add({
      targets: container,
      y: centerY - 15,
      duration: 3000,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1
    });
    
    // Slow rotation
    this.tweens.add({
      targets: container,
      angle: 360,
      duration: 120000,
      repeat: -1
    });
  }
  
  private createTitle(): void {
    const titleY = this.height * 0.12;
    
    // Glow behind title
    const titleGlow = this.add.text(this.width / 2, titleY, GAME_NAME, {
      fontSize: '52px',
      fontFamily: UI.FONT_FAMILY,
      color: '#00d4ff',
      fontStyle: 'bold',
      resolution: this.textRes
    }).setOrigin(0.5).setAlpha(0.3).setDepth(30);
    
    // Main title
    this.title = this.add.text(this.width / 2, titleY, GAME_NAME, {
      fontSize: '52px',
      fontFamily: UI.FONT_FAMILY,
      color: '#ffffff',
      fontStyle: 'bold',
      resolution: this.textRes,
      stroke: '#00d4ff',
      strokeThickness: 2
    }).setOrigin(0.5).setDepth(31);
    
    // Pulsing glow
    this.tweens.add({
      targets: titleGlow,
      alpha: { from: 0.2, to: 0.5 },
      scaleX: { from: 1, to: 1.02 },
      scaleY: { from: 1, to: 1.02 },
      duration: 1500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1
    });
    
    // Subtitle
    this.add.text(this.width / 2, this.height * 0.18, '🚀 Tap to launch between planets!', {
      fontSize: '18px',
      fontFamily: UI.FONT_FAMILY,
      color: '#94a3b8',
      resolution: this.textRes
    }).setOrigin(0.5).setDepth(30);
  }
  
  private createPlayButton(): void {
    const centerY = this.height * 0.72;
    
    this.playButton = this.add.container(this.width / 2, centerY);
    this.playButton.setDepth(50);
    
    const buttonWidth = 220;
    const buttonHeight = 70;
    
    // Button glow
    const buttonGlow = this.add.graphics();
    buttonGlow.fillStyle(0x00d4ff, 0.3);
    buttonGlow.fillRoundedRect(-buttonWidth / 2 - 10, -buttonHeight / 2 - 10, buttonWidth + 20, buttonHeight + 20, 22);
    
    // Gradient button background
    const buttonBg = this.add.graphics();
    
    // Outer border gradient effect
    buttonBg.lineStyle(4, 0x22d3ee, 1);
    buttonBg.strokeRoundedRect(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight, 16);
    
    // Inner fill with gradient simulation
    buttonBg.fillStyle(0x0891b2, 1);
    buttonBg.fillRoundedRect(-buttonWidth / 2 + 2, -buttonHeight / 2 + 2, buttonWidth - 4, buttonHeight - 4, 14);
    
    // Top highlight
    buttonBg.fillStyle(0x06b6d4, 0.8);
    buttonBg.fillRoundedRect(-buttonWidth / 2 + 4, -buttonHeight / 2 + 4, buttonWidth - 8, buttonHeight / 2 - 8, { tl: 12, tr: 12, bl: 0, br: 0 });
    
    // Play text
    const buttonText = this.add.text(0, 0, '▶  PLAY', {
      fontSize: '32px',
      fontFamily: UI.FONT_FAMILY,
      color: '#ffffff',
      fontStyle: 'bold',
      resolution: this.textRes
    }).setOrigin(0.5);
    
    this.playButton.add([buttonGlow, buttonBg, buttonText]);
    
    const hitArea = new Phaser.Geom.Rectangle(-buttonWidth / 2, -buttonHeight / 2, buttonWidth, buttonHeight);
    this.playButton.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);
    
    // Hover effects
    this.playButton.on('pointerover', () => {
      this.tweens.add({
        targets: this.playButton,
        scaleX: 1.08,
        scaleY: 1.08,
        duration: 150,
        ease: 'Back.easeOut'
      });
      this.tweens.add({
        targets: buttonGlow,
        alpha: 1.5,
        duration: 150
      });
    });
    
    this.playButton.on('pointerout', () => {
      this.tweens.add({
        targets: this.playButton,
        scaleX: 1,
        scaleY: 1,
        duration: 150
      });
      this.tweens.add({
        targets: buttonGlow,
        alpha: 1,
        duration: 150
      });
    });
    
    this.playButton.on('pointerdown', () => {
      this.startGame();
    });
    
    // Breathing pulse animation
    this.tweens.add({
      targets: this.playButton,
      scaleX: { from: 1, to: 1.03 },
      scaleY: { from: 1, to: 1.03 },
      duration: 1200,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1
    });
    
    // Glow pulse
    this.tweens.add({
      targets: buttonGlow,
      alpha: { from: 0.8, to: 1.3 },
      duration: 1200,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1
    });
    
    // Tap anywhere hint
    this.add.text(this.width / 2, centerY + 55, 'or tap anywhere', {
      fontSize: '14px',
      fontFamily: UI.FONT_FAMILY,
      color: '#64748b',
      resolution: this.textRes
    }).setOrigin(0.5).setDepth(50);
  }
  
  private createHighScoreDisplay(): void {
    const highScore = this.registry.get('highScore') || 0;
    
    // Container for stats
    const statsY = this.height * 0.88;
    
    // Trophy and best score
    const bestText = this.add.text(this.width / 2, statsY, `🏆 Best: ${highScore}`, {
      fontSize: '22px',
      fontFamily: UI.FONT_FAMILY,
      color: '#fbbf24',
      fontStyle: 'bold',
      resolution: this.textRes
    }).setOrigin(0.5).setDepth(30);
    
    // Games played
    const gamesPlayed = this.registry.get('gamesPlayed') || 0;
    this.add.text(this.width / 2, statsY + 28, `🎮 ${gamesPlayed} games played`, {
      fontSize: '14px',
      fontFamily: UI.FONT_FAMILY,
      color: '#64748b',
      resolution: this.textRes
    }).setOrigin(0.5).setDepth(30);
    
    // Highlight if high score exists
    if (highScore > 0) {
      this.tweens.add({
        targets: bestText,
        scale: { from: 1, to: 1.05 },
        duration: 800,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1
      });
    }
  }
  
  private createSettingsButton(): void {
    const soundEnabled = this.registry.get('soundEnabled') !== false;
    
    this.settingsButton = this.add.container(this.width - 50, 50);
    this.settingsButton.setDepth(100);
    
    // Glass effect background
    const bg = this.add.circle(0, 0, 28, 0x1e293b, 0.8);
    bg.setStrokeStyle(2, 0x334155, 1);
    
    const icon = this.add.text(0, 0, soundEnabled ? '🔊' : '🔇', {
      fontSize: '22px',
      resolution: this.textRes
    }).setOrigin(0.5);
    
    this.settingsButton.add([bg, icon]);
    
    bg.setInteractive({ useHandCursor: true });
    
    bg.on('pointerover', () => {
      this.tweens.add({ targets: bg, scale: 1.1, duration: 100 });
    });
    
    bg.on('pointerout', () => {
      this.tweens.add({ targets: bg, scale: 1, duration: 100 });
    });
    
    bg.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointer.event.stopPropagation();
      const newState = !this.registry.get('soundEnabled');
      this.registry.set('soundEnabled', newState);
      localStorage.setItem('sticky_planet_sound', String(newState));
      icon.setText(newState ? '🔊' : '🔇');
    });
  }
  
  private createSparkles(): void {
    // Occasional sparkle particles
    this.time.addEvent({
      delay: 300,
      callback: () => {
        const x = Phaser.Math.Between(0, this.width);
        const y = Phaser.Math.Between(0, this.height);
        
        const sparkle = this.add.circle(x, y, 2, 0xffffff, 0.8);
        sparkle.setDepth(15);
        
        this.tweens.add({
          targets: sparkle,
          alpha: 0,
          scale: 2,
          duration: 600,
          ease: 'Quad.easeOut',
          onComplete: () => sparkle.destroy()
        });
      },
      loop: true
    });
  }
  
  private playEntranceAnimation(): void {
    // Fade in
    this.cameras.main.fadeIn(500);
    
    // Title slide in
    this.title.setY(this.height * 0.05);
    this.title.setAlpha(0);
    this.tweens.add({
      targets: this.title,
      y: this.height * 0.12,
      alpha: 1,
      duration: 800,
      ease: 'Back.easeOut',
      delay: 200
    });
    
    // Play button pop in
    this.playButton.setScale(0);
    this.tweens.add({
      targets: this.playButton,
      scale: 1,
      duration: 600,
      ease: 'Back.easeOut',
      delay: 400
    });
  }
  
  private setupInput(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      // Ignore if clicking settings button area
      const settingsArea = new Phaser.Geom.Circle(this.width - 50, 50, 40);
      if (settingsArea.contains(pointer.x, pointer.y)) {
        return;
      }
      
      this.startGame();
    });
    
    this.input.keyboard?.on('keydown-SPACE', () => this.startGame());
    this.input.keyboard?.on('keydown-ENTER', () => this.startGame());
  }
  
  private startGame(): void {
    console.log('[MenuScene] Starting game...');
    
    const gamesPlayed = (this.registry.get('gamesPlayed') || 0) + 1;
    this.registry.set('gamesPlayed', gamesPlayed);
    localStorage.setItem('sticky_planet_games_played', String(gamesPlayed));
    
    // Play button flash effect
    this.tweens.add({
      targets: this.playButton,
      scale: 1.2,
      alpha: 0,
      duration: 200,
      ease: 'Quad.easeIn'
    });
    
    this.cameras.main.fadeOut(400, 0, 0, 0);
    
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENES.GAME);
    });
  }
  
  update(): void {
    // Planet animations are handled by tweens - no update needed
  }
  
  shutdown(): void {
    this.scale.off('resize', this.handleResize, this);
    if (this.shootingStarTimer) {
      this.shootingStarTimer.destroy();
    }
  }
}

export default MenuScene;
